import React, { useEffect } from 'react';

import { connect } from 'react-redux';
import { Button, Form } from 'react-bootstrap';
import { Drawer } from 'rsuite';
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import { toast } from 'react-toastify';
import * as FileSaver from 'file-saver';

import { Loading, PageHeader, Pagination } from '../../components';
import DataTable from '../../components/DataTable/DataTable';
import RowPaginator from '../../components/RowPaginator/RowPaginator';
import PermissionGate from '../../components/PermissionGate/PermissionGate';

import useComponentState from '../../utils/useComponentState';
import {
  getAllFailedBatchRecords,
  searchFailedBatchRecords,
  downloadFailedBatchRecords,
} from '../../api/failedBatchRecords';
import { FAILED_BATCH_PERMISSIONS } from '../../constants/permissions';
import {
  EMPTY_FILTERS,
  TYPE_OPTIONS,
  DOWNLOAD_PAGE_SIZE,
} from './FailedBatchRecordsConstants';
import { convertIsoDatesInText } from '../../utils/dataFormatter';

import styles from './FailedBatchRecords.module.scss';

const FailedBatchRecords = (props) => {
  const { auth: { token } } = props.auth;

  const [state, setState] = useComponentState({
    tableHeading: {
      column: ['Batch ID', 'File Name', 'Type', 'Account ID', 'MCC Code', 'Reason'],
    },
    recordsData: [],
    recordsLoadingError: '',
    loading: false,
    currentPage: 0,
    totalPages: 0,
    rowSize: 25,

    filters: EMPTY_FILTERS,
    isFiltered: false,

    showDrawer: false,
    drawerData: null,

    downloadStatus: '',
  });

  useEffect(() => {
    fetchData(0, false);
  }, []);

  const fetchData = async (page = 0, isSearch = false, filters = state.filters, pageSize = state.rowSize) => {
    setState({ loading: true, recordsData: [], recordsLoadingError: '' });
    try {
      let response;
      if (isSearch) {
        response = await searchFailedBatchRecords(token, { ...filters, page, pageSize });
      } else {
        response = await getAllFailedBatchRecords(token, page, pageSize);
      }
      setState({
        recordsData: response.list || [],
        totalPages: response.totalPageCount || 0,
        currentPage: page,
      });
    } catch (err) {
      const notFound = err?.status === 404 || err?.message === 'BATCH_FAILED_RECORDS_NOT_FOUND';
      setState({
        recordsLoadingError: notFound
          ? 'No records found.'
          : 'Oops, something went wrong. Please try again.',
      });
    }
    setState({ loading: false });
  };

  const hasActiveFilters = (filters) => Object.values(filters).some((value) => value);

  const handleFilterChange = (field, value) => {
    setState({ filters: { ...state.filters, [field]: value } });
  };

  const handleTypeChange = (value) => {
    const newFilters = { ...state.filters, type: value };
    setState({ filters: newFilters, currentPage: 0 });
    if (hasActiveFilters(newFilters)) {
      setState({ isFiltered: true });
      fetchData(0, true, newFilters);
    } else {
      setState({ isFiltered: false });
      fetchData(0, false, newFilters);
    }
  };

  const handleSearch = () => {
    if (hasActiveFilters(state.filters)) {
      setState({ isFiltered: true, currentPage: 0 });
      fetchData(0, true, state.filters);
    } else if (state.isFiltered) {
      setState({ filters: EMPTY_FILTERS, isFiltered: false, currentPage: 0 });
      fetchData(0, false, EMPTY_FILTERS);
    }
  };

  const handleClear = () => {
    setState({ filters: EMPTY_FILTERS, isFiltered: false, currentPage: 0 });
    fetchData(0, false, EMPTY_FILTERS);
  };

  const handlePagination = (page) => {
    setState({ currentPage: page });
    fetchData(page, state.isFiltered, state.filters);
  };

  const handleRowPaginationChange = (event) => {
    const newRowSize = Number(event.target.value);
    setState({ rowSize: newRowSize, currentPage: 0 });
    fetchData(0, state.isFiltered, state.filters, newRowSize);
  };

  const handleDownload = async () => {
    setState({ downloadStatus: 'DOWNLOADING' });
    try {
      const blob = await downloadFailedBatchRecords(token, {
        ...state.filters,
        page: 0,
        pageSize: DOWNLOAD_PAGE_SIZE,
      });
      const csvText = await blob.text();
      const formattedCsv = convertIsoDatesInText(csvText);
      const formattedBlob = new Blob([formattedCsv], { type: blob.type || 'text/csv' });
      FileSaver.saveAs(formattedBlob, 'Failed_Batch_Records_Filtered.csv');
      setState({ downloadStatus: '' });
    } catch (err) {
      setState({ downloadStatus: '' });
      toast.error(err?.message || 'Failed to download. Please try again.');
    }
  };

  const handleDrawerOpen = (rowData) => {
    if (!rowData) return;
    setState({ showDrawer: true, drawerData: rowData });
  };

  const handleDrawerClose = () => {
    setState({ showDrawer: false, drawerData: null });
  };

  const renderSearch = () => (
    <Form className={styles.filter_form}>
      <Form.Group className={styles.filter_field}>
        <Form.Label>Batch ID</Form.Label>
        <Form.Control
          type='number'
          placeholder='Batch ID'
          value={state.filters.batchId}
          onChange={(e) => handleFilterChange('batchId', e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          maxLength={50}
        />
      </Form.Group>

      <Form.Group className={styles.filter_field}>
        <Form.Label>Type</Form.Label>
        <Form.Control
          as='select'
          className={styles.type_select}
          value={state.filters.type}
          onChange={(e) => handleTypeChange(e.target.value)}
        >
          <option value=''>All Types</option>
          {TYPE_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Form.Control>
      </Form.Group>

      <Form.Group className={styles.filter_field}>
        <Form.Label>File Name</Form.Label>
        <Form.Control
          type='text'
          placeholder='File Name'
          value={state.filters.fileName}
          onChange={(e) => handleFilterChange('fileName', e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          maxLength={100}
        />
      </Form.Group>

      <Form.Group className={styles.filter_field}>
        <Form.Label>Account ID</Form.Label>
        <Form.Control
          type='text'
          placeholder='Account ID'
          value={state.filters.accountId}
          onChange={(e) => handleFilterChange('accountId', e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          maxLength={50}
        />
      </Form.Group>

      <div className={styles.filter_actions}>
        <Button variant='secondary' onClick={handleSearch}>
          <FontAwesomeIcon icon={faSearch} /> Search
        </Button>
        {state.isFiltered && (
          <Button variant='outline-secondary' onClick={handleClear}>
            <FontAwesomeIcon icon={faTimes} /> Clear
          </Button>
        )}
        {state.isFiltered && (
          <PermissionGate
            permission={FAILED_BATCH_PERMISSIONS.FAILED_RECORDS.DOWNLOAD}
          >
            <Button
              variant='secondary'
              onClick={handleDownload}
              disabled={state.downloadStatus === 'DOWNLOADING'}
            >
              {state.downloadStatus === 'DOWNLOADING'
                ? 'Downloading...'
                : 'Download'}
            </Button>
          </PermissionGate>
        )}
      </div>
    </Form>
  );

  const renderDrawer = () => {
    const data = state.drawerData;
    if (!data) return null;

    return (
      <>
        <Drawer.Header>
          <div className={styles.drawer_header}>
            <Drawer.Title>Failed Batch Record Details</Drawer.Title>
          </div>
        </Drawer.Header>

        <Drawer.Body>
          <div className={styles.drawer_body}>
            <div className={styles.detail_row}>
              <strong>ID:</strong>
              <span>{data.id}</span>
            </div>
            <div className={styles.detail_row}>
              <strong>Batch ID:</strong>
              <span>{data.batchId ?? 'N/A'}</span>
            </div>
            <div className={styles.detail_row}>
              <strong>File Name:</strong>
              <span>{data.fileName || 'N/A'}</span>
            </div>
            <div className={styles.detail_row}>
              <strong>Type:</strong>
              <span>{data.type || 'N/A'}</span>
            </div>
            <div className={styles.detail_row}>
              <strong>Account ID:</strong>
              <span>{data.accountId || 'N/A'}</span>
            </div>
            <div className={styles.detail_row}>
              <strong>Current Mobile No:</strong>
              <span>{data.currentMobileNo || 'N/A'}</span>
            </div>
            <div className={styles.detail_row}>
              <strong>Previous Mobile No:</strong>
              <span>{data.previousMobileNo || 'N/A'}</span>
            </div>
            <div className={styles.detail_row}>
              <strong>User ID:</strong>
              <span>{data.userId ?? 'N/A'}</span>
            </div>
            <div className={styles.detail_row}>
              <strong>Cycle ID:</strong>
              <span>{data.cycleId || 'N/A'}</span>
            </div>
            <div className={styles.detail_row}>
              <strong>MCC Code:</strong>
              <span>{data.mccCode || 'N/A'}</span>
            </div>
            <div className={styles.detail_row}>
              <strong>Billing Value:</strong>
              <span>{data.billingValue || 'N/A'}</span>
            </div>
            <div className={styles.detail_row}>
              <strong>Reason:</strong>
              <span>{data.reason || 'N/A'}</span>
            </div>
            <div className={styles.detail_row}>
              <strong>Created At:</strong>
              <span>{data.createdAt ? moment(data.createdAt).format('DD/MM/YYYY HH:mm') : 'N/A'}</span>
            </div>
          </div>
        </Drawer.Body>
      </>
    );
  };

  const { tableHeading, recordsData, recordsLoadingError, loading, currentPage, totalPages, rowSize } = state;

  return (
    <div className={styles.root_div}>
      <div>
        <PageHeader text='Failed Batch Records' />
        <div className={styles.search_area_block}>
          <div className={styles.user_input}>
            <RowPaginator
              handleRowPaginationChange={handleRowPaginationChange}
              rowSize={rowSize}
            />
            <div className={styles.right}>
              {renderSearch()}
            </div>
          </div>
        </div>
        {loading ? (
          <Loading text='Loading' />
        ) : (
          <>
            <DataTable
              tableData={recordsData}
              tableheading={tableHeading}
              handleOnClick={handleDrawerOpen}
              tag='failed-batch-records'
              fetchError={recordsLoadingError}
              notFoundMessage={recordsLoadingError}
              search_filter_loading={false}
            />
            {!recordsLoadingError && totalPages > 0 && (
              <div className={styles.dualPaginateContainer}>
                <div className={styles.pages}>
                  <span>
                    Page {currentPage + 1} of {totalPages}
                  </span>
                  &nbsp;&nbsp;&nbsp;
                  <Pagination
                    pageCount={totalPages}
                    onChange={handlePagination}
                    currentPage={currentPage}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Drawer show={state.showDrawer} onHide={handleDrawerClose} placement='right'>
        {renderDrawer()}
      </Drawer>
    </div>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(FailedBatchRecords);
